import { useQuery } from "@tanstack/react-query";
import type { RecipientSuggestion } from "@websede/contracts";
import { type KeyboardEvent, useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "../api/client.js";

/**
 * Comma-separated address field with suggestions for the address being typed. Arrow keys move,
 * Enter or Tab picks, Escape closes. Picking replaces the current token with the address.
 * Without suggestions, Enter finishes the address being typed; it never sends the message.
 */
export function RecipientInput({
  value,
  onChange,
  autoFocus = false,
  required = false,
  placeholder,
  bare = false,
  label,
}: {
  value: string;
  onChange: (value: string) => void;
  autoFocus?: boolean;
  required?: boolean;
  placeholder?: string;
  /** Borderless, for the compose window's one-line fields. */
  bare?: boolean;
  label?: string;
}) {
  const { t } = useTranslation();
  const listId = useId();
  const token = currentToken(value);
  const debounced = useDebounced(token, 150);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const field = useRef<HTMLInputElement>(null);
  const suggestions = useQuery({
    queryKey: ["mail", "recipients", debounced],
    queryFn: () => api.mail.recipients(debounced),
    enabled: open && debounced.length >= 1,
    staleTime: 30_000,
  });
  const items = token.length >= 1 ? (suggestions.data?.suggestions ?? []) : [];
  const showList = open && items.length > 0;

  useEffect(() => {
    if (autoFocus) field.current?.focus();
  }, [autoFocus]);

  const pick = (suggestion: RecipientSuggestion) => {
    onChange(replaceToken(value, suggestion.email));
    setOpen(false);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (!showList) {
      if (event.key === "Enter") {
        event.preventDefault();
        onChange(finishToken(value));
      }
      return;
    }
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActive((i) => (i + 1) % items.length);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActive((i) => (i - 1 + items.length) % items.length);
    } else if (event.key === "Enter" || event.key === "Tab") {
      const chosen = items[active];
      if (chosen) {
        event.preventDefault();
        pick(chosen);
      }
    } else if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      setOpen(false);
    }
  };

  return (
    <div className="relative min-w-0 flex-1">
      <input
        ref={field}
        aria-label={label}
        className={
          bare
            ? "w-full bg-transparent py-2 text-sm outline-none"
            : "w-full rounded-md border border-rule bg-surface px-3 py-2 text-sm outline-none focus:border-accent"
        }
        value={value}
        required={required}
        autoComplete="off"
        spellCheck={false}
        placeholder={placeholder}
        role="combobox"
        aria-expanded={showList}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={showList ? `${listId}-${active}` : undefined}
        onChange={(event) => {
          onChange(event.target.value);
          setOpen(true);
          setActive(0);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onKeyDown={onKeyDown}
      />
      {showList ? (
        <div
          id={listId}
          role="listbox"
          className="absolute inset-x-0 top-full z-30 mt-1 max-h-72 overflow-auto rounded-md border border-rule bg-surface py-1 shadow-lg"
        >
          {items.map((s, index) => (
            <div
              key={s.email}
              id={`${listId}-${index}`}
              role="option"
              tabIndex={-1}
              aria-selected={index === active}
              className={`flex cursor-pointer items-center justify-between gap-3 px-3 py-1.5 text-sm ${index === active ? "bg-accent-soft" : ""}`}
              onMouseEnter={() => setActive(index)}
              // mousedown keeps focus in the field; a click would blur and close the list first.
              onMouseDown={(event) => {
                event.preventDefault();
                pick(s);
              }}
            >
              <span className="min-w-0">
                <span className="block truncate font-medium">{s.name ?? s.email}</span>
                {s.name ? (
                  <span className="block truncate text-xs text-ink-3">{s.email}</span>
                ) : null}
              </span>
              <span className="shrink-0 text-[11px] text-ink-3">
                {t(`mail.recipientSource.${s.source}`)}
              </span>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

/** The address being typed: everything after the last separator. */
export function currentToken(value: string): string {
  const parts = value.split(/[,;]/);
  return (parts[parts.length - 1] ?? "").trim();
}

export function replaceToken(value: string, email: string): string {
  const cut = Math.max(value.lastIndexOf(","), value.lastIndexOf(";"));
  const head = cut >= 0 ? value.slice(0, cut + 1) : "";
  return `${head}${head ? " " : ""}${email}, `;
}

/** Ends the address being typed with a separator, as Enter does; an empty token is left alone. */
export function finishToken(value: string): string {
  const token = currentToken(value);
  return token ? replaceToken(value, token) : value;
}

/** Splits the field into addresses, accepting "Name <addr>" as well as bare addresses. */
export function parseRecipients(value: string): Array<{ email: string; name?: string }> {
  return value
    .split(/[,;]/)
    .map((part) => part.trim())
    .filter(Boolean)
    .flatMap((part) => {
      const angled = /^(.*)<([^>]+)>$/.exec(part);
      if (angled) {
        const name = (angled[1] ?? "").trim().replace(/^"|"$/g, "");
        const email = (angled[2] ?? "").trim();
        return [name ? { email, name } : { email }];
      }
      return part
        .split(/\s+/)
        .filter(Boolean)
        .map((email) => ({ email }));
    });
}

/** Entries that are not an address, so the composer can say which before the server refuses. */
export function invalidRecipients(value: string): string[] {
  return parseRecipients(value)
    .map((r) => r.email)
    .filter((email) => !/^[^\s@<>(),;:"]+@[^\s@<>(),;:"]+\.[^\s@<>(),;:"]{2,}$/.test(email));
}

function useDebounced<T>(value: T, ms: number): T {
  const [current, setCurrent] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setCurrent(value), ms);
    return () => clearTimeout(timer);
  }, [value, ms]);
  return current;
}
